import axios, { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Environment } from '../../../environment';
import { jwtDecode } from 'jwt-decode';
import { Api } from '../axios-config';

export interface IListagemConfProducao {
  idconf: string;
  descricao: string;
  vlproducao: number;
  meta: number;
  acmeta: number;
  vladicional: number;
  status: boolean;
}

export interface IConfProducao {
  idproducao: string;
  vlproducao: number;
  meta: number;
  acmeta: number;
  vladicional: number;
  status: boolean;
}

export interface IProducaoList {
  idproducao: string;
  descricao: string;
}

type TCeiComTotalCount = {
  data: IListagemConfProducao[];
  totalCount: number;
}

interface ErrorResponse {
  error: {
    default: string;
  };
}

interface JwtPayload {
  faz: string;
  loc: string;
}

export interface IListagemProducao {
  idproducao: string;
  descricao: string;
}

export interface IListagemConfProducao {
  idconf: string;
  descricao: string;
}

type TotalCount = {
  data: IListagemProducao[];
  totalCount: number;
}

type TotalCountConf = {
  data: IListagemConfProducao[];
  totalCount: number;
}

const getAll = async (page = 1, filter = ''): Promise<TCeiComTotalCount | Error> => {
  try {
    let idfazenda;
    const accessToken = localStorage.getItem('APP_ACCESS_TOKEN');
    if (accessToken) {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      idfazenda = decoded.faz;
    }
    const urlRelativa = `/confproducao?page=${page}&limit=${Environment.LIMITE_DE_LINHAS1}&filter=${filter}&idfazenda=${idfazenda}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_LINHAS1),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const getById = async (id: string): Promise<IConfProducao | Error> => {
  try {
    const { data } = await Api.get(`/confproducao/${id}`);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const create = async (dados: IConfProducao): Promise<string | Error> => {
  try {
    let idfazenda;
    const accessToken = localStorage.getItem('APP_ACCESS_TOKEN');
    if (accessToken) {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      idfazenda = decoded.faz;
    }
    const guuid = uuidv4();
    const { data } = await Api.post<IConfProducao>('/confproducao', { idconf: guuid, idfazenda: idfazenda, ...dados });
    if (Object.keys(data).length === 0) {
      return guuid;
    }
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const updateById = async (id: string, dados: IConfProducao): Promise<void | Error> => {
  try {
    await Api.put(`/confproducao/${id}`, dados);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const deleteById = async (id: string): Promise<void | Error> => {
  try {
    await Api.delete(`/confproducao/${id}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const getAlllist = async (id = '', filter = ''): Promise<TotalCount | Error> => {
  try {
    const urlRelativa: string = id === '' ? `/atproducao?limit=${Environment.LIMITE_DE_CADASTRO}&filter=${filter}` : `/atproducaolist/${id}`;

    const { data, headers } = await Api.get(urlRelativa);

    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_CADASTRO),
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const getAlllistConf = async (id = '', filter = ''): Promise<TotalCountConf | Error> => {
  try {
    let idfazenda;
    const accessToken = localStorage.getItem('APP_ACCESS_TOKEN');
    if (accessToken) {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      idfazenda = decoded.faz;
    }
    const urlRelativa: string = id === '' ? `/confproducaolist?limit=${Environment.LIMITE_DE_CADASTRO}&filter=${filter}&idfazenda=${idfazenda}` : `/confproducaolist/${id}`;

    const { data, headers } = await Api.get(urlRelativa);

    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_CADASTRO),
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

export const ProducaoService = {
  getAll,
  create,
  getById,
  updateById,
  deleteById,
  getAlllist,
  getAlllistConf,
}; 